
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, MapPin } from 'lucide-react';
import GeoPromotionMapPicker from './GeoPromotionMapPicker';
import type { GeoPromotionLocation } from '@/hooks/useAdminGeoPromotions';

interface GeoPromotionLocationListProps {
  locations: GeoPromotionLocation[];
  onChange: (locations: GeoPromotionLocation[]) => void;
}

const emptyLocation: GeoPromotionLocation = {
  latitude: 0,
  longitude: 0,
  radius_meters: 2000,
  label: '',
};

const GeoPromotionLocationList: React.FC<GeoPromotionLocationListProps> = ({ locations, onChange }) => {
  const { t } = useTranslation();

  const handleAdd = () => {
    onChange([...locations, { ...emptyLocation }]);
  };

  const handleRemove = (index: number) => {
    if (locations.length <= 1) return;
    onChange(locations.filter((_, i) => i !== index));
  };

  const handleUpdate = (index: number, updates: Partial<GeoPromotionLocation>) => {
    onChange(locations.map((loc, i) => i === index ? { ...loc, ...updates } : loc));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5 text-sm font-medium">
          <MapPin className="h-4 w-4" />
          {t('geoPromotions.locations', 'Locations')}
          <span className="text-muted-foreground font-normal">({locations.length})</span>
        </Label>
        <Button type="button" variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          {t('geoPromotions.addLocation', 'Add location')}
        </Button>
      </div>

      {locations.map((loc, index) => (
        <Card key={index} className="border-dashed">
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-muted-foreground">
                {t('geoPromotions.locationNumber', 'Location {{number}}', { number: index + 1 })}
              </Label>
              {locations.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => handleRemove(index)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>

            <div>
              <Label className="text-xs">{t('geoPromotions.locationLabel', 'Label (optional)')}</Label>
              <Input
                value={loc.label}
                onChange={(e) => handleUpdate(index, { label: e.target.value })}
                placeholder={t('geoPromotions.locationLabelPlaceholder', 'e.g. North Branch, Downtown...')}
                className="text-sm"
              />
            </div>

            <GeoPromotionMapPicker
              latitude={loc.latitude}
              longitude={loc.longitude}
              radiusMeters={loc.radius_meters}
              onLocationChange={(lat, lng) => handleUpdate(index, { latitude: lat, longitude: lng })}
            />

            <div>
              <Label className="text-xs">{t('geoPromotions.radius', 'Radius')} (m)</Label>
              <Input
                type="number"
                value={loc.radius_meters}
                onChange={(e) => handleUpdate(index, { radius_meters: Number(e.target.value) })}
                className="text-sm"
              />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default GeoPromotionLocationList;
